import { collection, doc, getDocs, setDoc, deleteDoc } from 'firebase/firestore'
import type { Station } from '../../types'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'

const stationsCacheByPlace: Record<string, Station[]> = {}

function stationsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'stations')
}

function stationDoc(placeId: string, stationId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'stations', stationId)
}

export async function primeStationsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  const snap = await getDocs(stationsRef(placeId))
  stationsCacheByPlace[placeId] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Station))
}

export function getStations(): Station[] {
  const placeId = getCurrentPlaceId()
  return stationsCacheByPlace[placeId] ?? []
}

export function saveStations(stations: Station[]): void {
  const placeId = getCurrentPlaceId()
  stationsCacheByPlace[placeId] = stations
  ;(async () => {
    const snap = await getDocs(stationsRef(placeId))
    const ids = new Set(stations.map((s) => s.id))
    for (const d of snap.docs) {
      if (!ids.has(d.id)) await deleteDoc(d.ref)
    }
    for (const s of stations) {
      await setDoc(stationDoc(placeId, s.id), s)
    }
  })().catch((e) => console.error('Firestore saveStations failed', e))
}

function generateStationId(): string {
  return `S-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addStation(station: Omit<Station, 'id'>): Station {
  const placeId = getCurrentPlaceId()
  const id = generateStationId()
  const full: Station = { ...station, id }
  const cache = stationsCacheByPlace[placeId] ?? []
  stationsCacheByPlace[placeId] = [...cache, full]
  setDoc(stationDoc(placeId, id), full).catch((e) =>
    console.error('Firestore addStation failed', e),
  )
  return full
}

export function updateStation(id: string, patch: Partial<Station>): Station[] {
  const placeId = getCurrentPlaceId()
  const cache = stationsCacheByPlace[placeId] ?? []
  stationsCacheByPlace[placeId] = cache.map((s) => (s.id === id ? { ...s, ...patch } : s))
  const found = stationsCacheByPlace[placeId].find((s) => s.id === id)
  if (found) {
    setDoc(stationDoc(placeId, id), found).catch((e) =>
      console.error('Firestore updateStation failed', e),
    )
  }
  return stationsCacheByPlace[placeId]
}

export function deleteStation(id: string): Station[] {
  const placeId = getCurrentPlaceId()
  const cache = stationsCacheByPlace[placeId] ?? []
  stationsCacheByPlace[placeId] = cache.filter((s) => s.id !== id)
  deleteDoc(stationDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteStation failed', e),
  )
  return stationsCacheByPlace[placeId]
}

export function isStationsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in stationsCacheByPlace
}

/** Clear in-memory cache so the next user does not see previous account/place data. */
export function clearStationsCache(): void {
  Object.keys(stationsCacheByPlace).forEach((k) => delete stationsCacheByPlace[k])
}
